import buildNumbers from './build-numbers.json';
import React, {useState, useEffect, Suspense} from 'react';
import ReactDOM from 'react-dom';

import {DayMap, IGoal, IReason, IReport, ITactic} from './common';
import {Switch, Route, useLocation} from 'react-router-dom';
import {UserProvider, UserContextConsumer} from './UserProvider';
import Header from './Header';
import {
  analytics,
  firebaseApp,
  firebaseConfig,
  enableRecaptcha,
} from './firebase';
import {useMediaQuery} from '@material-ui/core';
import Privacy from './pages/Privacy';
import {logEvent, setCurrentScreen} from 'firebase/analytics';

import {createTheme, ThemeProvider} from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import {BrowserRouter} from 'react-router-dom';
import ServiceWorkerWrapper from './ServiceWorkerWrapper';

import bkImg from './img/bk.webp';
import headerImg from './img/header.webp';

import './app.scss';

const topLevelLazyCompletions: Promise<void>[] = [];
function lazyTopLevelImport<T extends Function>(importer: T) {
  return React.lazy(() => {
    let resolver: Function = () => {};
    topLevelLazyCompletions.push(
      new Promise(resolve => {
        resolver = resolve;
      })
    );
    const p = importer();
    p.then(() => resolver());
    return p;
  });
}

// Top level pages.
const Days = lazyTopLevelImport(() => import('./pages/Days'));
const Reasons = lazyTopLevelImport(() => import('./pages/Reasons'));
const Goals = lazyTopLevelImport(() => import('./pages/Goals'));
const Reports = lazyTopLevelImport(() => import('./pages/Reports'));
const Tactics = lazyTopLevelImport(() => import('./pages/Tactics'));
const Account = lazyTopLevelImport(() => import('./pages/Account'));
const About = lazyTopLevelImport(() => import('./pages/About'));
const Feedback = lazyTopLevelImport(() => import('./pages/Feedback'));
const Admin = lazyTopLevelImport(() => import('./pages/Admin'));

console.log(topLevelLazyCompletions);

// Load blog routing after any requested top level page loads.
let blogRoutesResolver: Function = () => {};
const blogRoutesComplete = new Promise(resolve => {
  blogRoutesResolver = resolve;
});
const BlogRoutes = React.lazy(() => {
  const prereq = topLevelLazyCompletions.length
    ? Promise.any(topLevelLazyCompletions)
    : Promise.resolve();
  const p = prereq.then(() => import('./blog/_BlogRoutes'));
  p.then(() => blogRoutesResolver());
  return p;
});

// Load footer after blog routing loads.
const Footer = React.lazy(() =>
  blogRoutesComplete.then(() => import('./Footer'))
);

// Additional lazy imports.
const FirestoreData = React.lazy(() => import('./firestore'));

const InnerApp: React.FC<{
  themeMode: string;
  setThemeMode: React.Dispatch<React.SetStateAction<string>>;
}> = ({themeMode, setThemeMode}) => {
  const [days, setDays] = useState<DayMap | null>(null);
  const [reasons, setReasons] = useState<IReason[]>([]);
  const [goals, setGoals] = useState<IGoal[] | null>(null);
  const [tactics, setTactics] = useState<ITactic[] | null>(null);
  const [reports, setReports] = useState<IReport[] | null>(null);

  useEffect(() => {
    const body = document.querySelector('body')!.style;
    body.setProperty(
      'background-color',
      themeMode === 'light' ? '#f4f4f4' : '#1c1e21'
    );
    body.setProperty('color', themeMode === 'light' ? '#080808' : '#f0f0f0');
  }, [themeMode]);

  useEffect(() => {
    const el = document.getElementsByTagName('noscript');
    if (el && el.length > 0) el[0].remove();
  }, []);

  const [currentPath, setCurrentPath] = useState('');
  const TrackPage: React.FC = () => {
    const location = useLocation();
    useEffect(() => {
      setCurrentPath(location.pathname + location.search + location.hash);
    }, [location]);
    return <></>;
  };

  useEffect(() => {
    if (!currentPath) return;
    if (analytics) {
      setCurrentScreen(analytics, currentPath);
      logEvent(analytics, 'page_route', {
        site_version: buildNumbers.shortVersion,
        path: currentPath,
      });
    }
  }, [currentPath]);

  useEffect(() => {
    if (!enableRecaptcha) {
      return;
    }
    import('firebase/app-check')
      .then(({initializeAppCheck, ReCaptchaEnterpriseProvider}) => {
        initializeAppCheck(firebaseApp, {
          provider: new ReCaptchaEnterpriseProvider(
            firebaseConfig.recaptchaEnterpriseKey
          ),
          isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
        });
      })
      .catch(error => {
        console.error('Unable to lazy-load firebase/app-check:', error);
      });
  }, []);

  const isWide = useMediaQuery('(min-width: 70em)');
  const isNotWide = useMediaQuery('(max-width: 70.1em)'); // Avoid CLS stutters.
  const detailContainer = React.useRef(null);
  return (
    <UserProvider>
      {(isWide || isNotWide) && (
        <>
          <Header />
          <div className="app-container">
            <TrackPage />
            <div className="mainPane">
              <Switch>
                <Route path={['/days', '/days.html']}>
                  <Suspense fallback="">
                    <Days detailContainer={detailContainer} days={days} />
                  </Suspense>
                </Route>
                <Route path={['/reasons', '/reasons.html']}>
                  <Suspense fallback="">
                    <Reasons reasons={reasons} />
                  </Suspense>
                </Route>
                <Route path={['/goals', '/goals.html']}>
                  <Suspense fallback="">
                    <Goals goals={goals} days={days} />
                  </Suspense>
                </Route>
                <Route path={['/tactics', '/tactics.html']}>
                  <Suspense fallback="">
                    <Tactics tactics={tactics ?? []} />
                  </Suspense>
                </Route>
                <Route path={['/reports', '/reports.html']}>
                  <Suspense fallback="">
                    <Reports reports={reports} days={days} />
                  </Suspense>
                </Route>
                <Route path={['/account', '/account.html']}>
                  <Suspense fallback="">
                    <Account detailContainer={detailContainer} />
                  </Suspense>
                </Route>
                <Route path={['/about', '/about.html']}>
                  <Suspense fallback="">
                    <About />
                  </Suspense>
                </Route>
                <Route path={['/privacy', '/privacy.html']}>
                  <Suspense fallback="">
                    <Privacy />
                  </Suspense>
                </Route>
                <Route path={['/feedback', '/feedback.html']}>
                  <Suspense fallback="">
                    <Feedback />
                  </Suspense>
                </Route>
                <Route path="/admin/:arg?">
                  <Suspense fallback="">
                    <Admin />
                  </Suspense>
                </Route>
              </Switch>
              <Suspense fallback="">
                <BlogRoutes />
              </Suspense>
              <UserContextConsumer>
                {context =>
                  context &&
                  context.authenticated &&
                  !!context.user && (
                    <Suspense fallback="">
                      <FirestoreData
                        currentUser={context.user!}
                        setUserDoc={context.setUserDoc!}
                        setDays={setDays}
                        setReasons={setReasons}
                        setGoals={setGoals}
                        setTactics={setTactics}
                        setReports={setReports}
                      />
                    </Suspense>
                  )
                }
              </UserContextConsumer>
            </div>
            <div ref={detailContainer} />
          </div>
          <Suspense fallback="">
            <Footer theme={themeMode} setTheme={setThemeMode} />
          </Suspense>
        </>
      )}
    </UserProvider>
  );
};

function App(): JSX.Element {
  useEffect(() => {
    // Remove noscript markup.
    const el = document.getElementsByTagName('noscript');
    if (el && el.length > 0) el[0].remove();

    // Preload images.
    const img1 = new Image();
    img1.src = bkImg;
    const img2 = new Image();
    img2.src = headerImg;
    Object.assign(window, {
      preloadedImages: [img1, img2],
    });

    // Preload manifest.
    const windowNav: any = window.navigator;
    windowNav.getInstalledRelatedApps?.call(navigator);
  }, []);

  const GetUserDefaultTheme = () => {
    const localStorageTheme = localStorage.getItem('theme');
    if (localStorageTheme === 'dark' || localStorageTheme === 'light') {
      return localStorageTheme;
    }
    // if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   return 'dark';
    // }
    return 'light';
  };

  const [themeMode, setThemeMode] = useState(GetUserDefaultTheme());
  const materialTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          type: themeMode === 'light' ? 'light' : 'dark',
          primary: {
            main: themeMode === 'light' ? '#2196f3' : '#57aff5',
          },
        },
        props: {
          MuiAccordion: {
            TransitionProps: {unmountOnExit: true},
          },
        },
        typography: {
          button: {
            textTransform: 'none',
          },
        },
      }),
    [themeMode]
  );

  return (
    <BrowserRouter>
      <CssBaseline />
      <ServiceWorkerWrapper />
      <main id="app-root">
        <ThemeProvider theme={materialTheme}>
          <div className={'theme ' + themeMode}>
            <Suspense fallback="">
              <InnerApp themeMode={themeMode} setThemeMode={setThemeMode} />
            </Suspense>
          </div>
        </ThemeProvider>
      </main>
    </BrowserRouter>
  );
}

// let cls = 0;
// new PerformanceObserver((entryList: {getEntries: () => any}) => {
//   for (const entry of entryList.getEntries()) {
//     if (!entry.hadRecentInput) {
//       cls += entry.value;
//       console.log('Current CLS value:', cls, entry);
//     }
//   }
// }).observe({type: 'layout-shift', buffered: true});

// new PerformanceObserver((entryList: {getEntries: () => any}) => {
//   for (const entry of entryList.getEntries()) {
//     console.log('LCP candidate:', entry.startTime, entry);
//   }
// }).observe({type: 'largest-contentful-paint', buffered: true});

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
);
